import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Lock, ShieldCheck, Sparkles, X } from "lucide-react";
import AppShell from "../components/AppShell";
import LegalFooter from "../components/LegalFooter";
import { api } from "../lib/api";
import { useEntitlements, invalidateEntitlements } from "../lib/entitlements";

/**
 * Trial-expired experience — the polished home for users whose 7-day free
 * trial has ended. Displays the same plan catalog as /pricing, activates the
 * chosen plan through the shared /subscription/select-plan endpoint, and then
 * refreshes the entitlement snapshot so purchased tools unlock in-place —
 * no logout, no manual refresh.
 *
 * Feature-access is always driven by the backend `compute_status` output
 * (plan → features), never by client-side flags, so a user who buys Empresa
 * gets Empresa tools only (not Pro), and vice versa.
 */
const PLAN_CATALOG = [
  {
    id: "independiente",
    name: "Independiente",
    price: 299,
    tagline: "Perfecto para freelancers y creadores.",
    features: [
      "Finanzas · Ingresos · Egresos · Caja",
      "Facturación CFDI 4.0",
      "Calendario y Declaraciones",
      "Proyectos y Reportes",
      "Academia contable",
      "Mi Contador IA (chat)",
    ],
    excluded: ["Escáner OCR", "Clientes / Proveedores", "Nómina", "Bóveda", "Portafolio"],
  },
  {
    id: "empresa",
    name: "Empresa",
    price: 699,
    tagline: "Para negocios y equipos que crecen.",
    highlight: true,
    features: [
      "Todo lo de Independiente, más:",
      "Escáner de tickets (OCR con IA)",
      "Clientes y Proveedores",
      "Nómina completa",
      "Mi Bóveda · Notas · Inventario · Productos",
      "Facturas Enviadas y Recibidas",
    ],
    excluded: ["Portafolio", "Multi-Empresa"],
  },
  {
    id: "pro",
    name: "Pro",
    price: 1499,
    tagline: "Sin límites. Múltiples empresas.",
    features: [
      "Todo lo de Empresa, más:",
      "Portafolio · Patrimonio · Activos",
      "Múltiples empresas en una cuenta",
      "Dashboards independientes por empresa",
      "Cambio instantáneo entre empresas",
      "Soporte prioritario",
    ],
  },
];

export default function TrialExpiredPage() {
  const navigate = useNavigate();
  const { status, refresh } = useEntitlements();
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  // If the user is already on a paid plan (or their trial is somehow still
  // active), send them straight to the dashboard — no dead-end screens.
  useEffect(() => {
    if (!status) return;
    if (status.is_paid || status.trial_active) {
      navigate("/", { replace: true });
    }
  }, [status, navigate]);

  const selectPlan = async (planId, paymentType = "monthly_recurring") => {
    navigate(`/checkout?plan=${planId}&type=${paymentType}`);
  };

  const discount = status?.promo_discount || 0;

  return (
    <AppShell>
      <div className="pricing-page" data-testid="trial-expired-page">
        <section className="trial-expired-hero" data-testid="trial-expired-hero">
          <div className="trial-expired-icon" aria-hidden="true"><Lock size={24} /></div>
          <small className="eyebrow">PRUEBA GRATUITA TERMINADA</small>
          <h1>Tu prueba gratuita <em>terminó.</em></h1>
          <p style={{ maxWidth: 620, margin: "10px auto 0" }}>
            Tu cuenta e información siguen <b>completamente seguras</b>. Para
            continuar usando Mi Contador, elige el plan que mejor se adapte a
            ti. Podrás cancelar cuando quieras.
          </p>
          <div className="trial-expired-safety" data-testid="trial-expired-safety">
            <ShieldCheck size={14} /> Tus datos, facturas, clientes y documentos siguen intactos.
          </div>
          {notice && <div className="pricing-notice success" data-testid="trial-expired-notice">{notice}</div>}
          {error && <div className="pricing-notice error" data-testid="trial-expired-error">{error}</div>}
        </section>

        <section className="pricing-grid" data-testid="trial-expired-plans">
          {PLAN_CATALOG.map((plan) => {
            const price = discount ? plan.price * (1 - discount / 100) : plan.price;
            return (
              <article
                key={plan.id}
                className={`pricing-card${plan.highlight ? " highlight" : ""}`}
                data-testid={`trial-expired-plan-${plan.id}`}
              >
                {plan.highlight && <span className="pricing-badge">Recomendado</span>}
                <h2>{plan.name}</h2>
                <p className="pricing-tagline">{plan.tagline}</p>
                <div className="pricing-price">
                  {discount > 0 && plan.price > 0 && <s>${plan.price} MXN</s>}
                  <b>${Math.round(price).toLocaleString("es-MX")}</b>
                  <small>MXN/mes</small>
                </div>
                <ul className="pricing-features">
                  {plan.features.map((f) => (
                    <li key={f}><Check size={14} color="#10b981" /> <span>{f}</span></li>
                  ))}
                  {(plan.excluded || []).map((f) => (
                    <li key={f} className="excluded"><X size={14} color="#94a3b8" /> <span>{f}</span></li>
                  ))}
                </ul>
                <div style={{ display: "grid", gap: 8 }}>
                  <button
                    className={`pricing-cta${plan.highlight ? " primary" : ""}`}
                    onClick={() => selectPlan(plan.id, "monthly_recurring")}
                    disabled={busy === `${plan.id}:monthly_recurring`}
                    data-testid={`trial-expired-select-${plan.id}`}
                  >
                    <Sparkles size={14} /> {busy === `${plan.id}:monthly_recurring` ? "Activando…" : "Pago mensual automático"}
                  </button>
                  <button
                    className="pricing-cta"
                    style={{ background: "#f8fafc", color: "#0f172a", border: "1px solid #e2e8f0" }}
                    onClick={() => selectPlan(plan.id, "monthly_manual")}
                    disabled={busy === `${plan.id}:monthly_manual`}
                    data-testid={`trial-expired-select-${plan.id}-manual`}
                  >
                    {busy === `${plan.id}:monthly_manual` ? "Activando…" : "Pagar solo este mes"}
                  </button>
                  <small style={{ font: '500 11px "Nunito", sans-serif', color: "#94a3b8", textAlign: "center" }}>
                    Sin renovación automática · 30 días de acceso
                  </small>
                </div>
              </article>
            );
          })}
        </section>

        <section className="trial-expired-help" data-testid="trial-expired-help">
          <p>
            ¿Tienes un código promocional o preguntas? Ve a{" "}
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              data-testid="trial-expired-goto-pricing"
              style={{ background: "transparent", border: 0, color: "#4169e1", font: '800 14px "Nunito", sans-serif', cursor: "pointer", textDecoration: "underline", padding: 0 }}
            >
              la página de planes completa
            </button>.
          </p>
        </section>

        <LegalFooter variant="pricing" />
      </div>
    </AppShell>
  );
}
